import React, { useEffect, useRef, useState } from "react";
import * as THREE from "three";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls.js";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader.js";

function Player({isVideoEnded, src360, onVideoEnd, videoId}) {
    const WALKING_DURATION = 2000;
    const glbPath = `${process.env.PUBLIC_URL}/models/screen_cube.glb`;

    const [walkingCurrentTime, setWalkingCurrentTime] = useState(0);

    const sceneRef = useRef();
    const cameraRef = useRef();
    const controlsRef = useRef();
    const rendererRef = useRef();
    const objectRef = useRef();
    
    // Scene setup
    useEffect(() => {
        const scene = new THREE.Scene();
        const camera = new THREE.PerspectiveCamera(80, 16 / 9, 0.1, 1000);
        camera.position.set(0, 0, 0);
        cameraRef.current = camera;
        
        const renderer = new THREE.WebGLRenderer({ antialias: true });
        renderer.outputColorSpace = THREE.LinearSRGBColorSpace;
        renderer.toneMappingExposure = 1;
        renderer.setClearColor(0x000000);
        rendererRef.current = renderer;
        
        const container = document.getElementById("threejs-container");
        if (!container) return;
        container.appendChild(renderer.domElement);
        
        const resizeRenderer = () => {
            const { clientWidth, clientHeight } = container;
            renderer.setSize(clientWidth, clientHeight);
            camera.aspect = clientWidth / clientHeight;
            camera.updateProjectionMatrix();
        };
        window.addEventListener("resize", resizeRenderer);
        resizeRenderer();
        
        // OrbitControls
        const controls = new OrbitControls(camera, renderer.domElement);
        controls.enableDamping = true;
        controls.dampingFactor = 0.1;
        controls.enableZoom = false;
        controls.enablePan = false;
        controls.enableRotate = true;
        controls.rotateSpeed = -0.3;

        controls.minPolarAngle = Math.PI / 2;
        controls.maxPolarAngle = Math.PI / 2;

        // 横方向の振り向きを ±90°（ラジアンで ±Math.PI / 2）に制限
        controls.minAzimuthAngle = -Math.PI / 2; // -90度
        controls.maxAzimuthAngle =  Math.PI / 2; // +90度

        controlsRef.current = controls;
        sceneRef.current = scene;

        resetCameraRotation();
        
        const tick = () => {
            requestAnimationFrame(tick);
            controls.update();
            renderer.render(scene, camera);
        };
        tick();
        
        return () => {
            window.removeEventListener("resize", resizeRenderer);
            container.removeChild(renderer.domElement);
            renderer.dispose();
        };
    }, []);

    useEffect(() => {
        const updateInterval = 125;
        const interval = setInterval(() => {
            if (isVideoEnded) return;
            setWalkingCurrentTime(prev => {
                const next = Math.min(prev + updateInterval, WALKING_DURATION);
                if (next === WALKING_DURATION) {setTimeout(() => onVideoEnd(), 0);};
                return next;
            });
        }, updateInterval);

        return () => clearInterval(interval);
    }, [isVideoEnded, onVideoEnd]);
    
    // Load sphere or GLB as 360 screen
    useEffect(() => {
        if (!sceneRef.current) return;
        
        const scene = sceneRef.current;
        if (objectRef.current) {
            scene.remove(objectRef.current);
            objectRef.current.traverse?.((child) => {
                if (child.isMesh) child.geometry.dispose();
                if (child.material?.map) child.material.map.dispose();
            });
        }
        
        const texture = new THREE.TextureLoader().load(src360);
        texture.flipY = false;
        texture.colorSpace = THREE.LinearSRGBColorSpace
        
        if (glbPath) {
            // GLB モデルを読み込む
            const loader = new GLTFLoader();
            loader.load(glbPath, (gltf) => {
                const model = gltf.scene;
                model.rotation.x = Math.PI * 2;
                model.traverse((child) => {
                    if (child.isMesh) {
                        child.material = new THREE.MeshBasicMaterial({
                            map: texture,
                            side: THREE.FrontSide,
                            toneMapped: true,
                        });
                    }
                });
                scene.add(model);
                objectRef.current = model;
            });
        }
        resetCameraRotation();
    }, [src360, glbPath, videoId]);

    const resetCameraRotation = () => {
        if (controlsRef.current) {
            controlsRef.current.reset();        // ← これがベスト
            controlsRef.current.target.set(0, 0, -0.005);
            controlsRef.current.update();
        }
    };
    
    // Reset camera rotation when video ends
    useEffect(() => {
        if (!isVideoEnded) {
            setWalkingCurrentTime(0)
        }
    }, [isVideoEnded]);
    
    return (
        <div className="video-player-wrapper">
            <div
            className="walking-video"
            style={{
                "--current-time": walkingCurrentTime,
                "--duration": WALKING_DURATION,
            }}
            >
                <div className="video-timebar">
                    <div className="video-timebar__bar"></div>
                </div>
            </div>
            
            <div
            id="threejs-container"
            className={`video-player`}
            style={{ width: "100%", height: "100%" }}
            ></div>
        </div>
    );
}

export default React.memo(Player);
